import { useMemo, useState } from "react";
import styled from "styled-components";
import { BadgeCheck, ChevronRight, LogOut, ShieldCheck, Sparkles, UserRound } from "lucide-react";

import Profile from "./Profile";
import KYCVerification from "./KYCVerification";
import WalletAccess from "./WalletAccess";
import { Button } from "./common/ui/button";
import { useAuth } from "src/hooks/useAuth";
import { useUserData } from "src/hooks/useUserData";
import { AccountContainer, AccountNav, AccountNavItem } from "./common/Styles";

const NAVITEMS = [{ name: "Profile" }, { name: "KYC Verification" }, { name: "Wallet Access" }];

const PROFILE_FIELDS = [
  { key: "firstname", label: "First name" },
  { key: "lastname", label: "Last name" },
  { key: "phone", label: "Phone" },
  { key: "country", label: "Country" },
  { key: "address", label: "Address" },
  { key: "city", label: "City" },
  { key: "trading_name", label: "Trade name" },
] as const;

const Account = () => {
  const [selectedItem, setSelectedItem] = useState<string>(NAVITEMS[0].name);
  const { userData } = useUserData();
  const { logout } = useAuth();

  const isProfile = selectedItem === NAVITEMS[0].name;
  const isKYC = selectedItem === NAVITEMS[1].name;
  const isWalletAccess = selectedItem === NAVITEMS[2].name;

  const profileCompletion = useMemo(() => {
    const filled = PROFILE_FIELDS.filter(({ key }) => String(userData?.[key] ?? "").trim()).length;
    return Math.round((filled / PROFILE_FIELDS.length) * 100);
  }, [userData]);

  const missingFields = useMemo(
    () =>
      PROFILE_FIELDS.filter(({ key }) => !String(userData?.[key] ?? "").trim())
        .map(({ label }) => label)
        .slice(0, 3),
    [userData],
  );

  const kycUploadedCount = useMemo(
    () =>
      [userData?.user_images?.profile_picture, userData?.user_images?.user_id_image].filter(Boolean)
        .length,
    [userData],
  );

  const kycReady = kycUploadedCount === 2;

  const fullName = useMemo(() => {
    const name = [userData?.firstname, userData?.lastname].filter(Boolean).join(" ").trim();
    return name || userData?.email || "Account holder";
  }, [userData]);

  const initials = useMemo(() => {
    const letters = [userData?.firstname, userData?.lastname]
      .filter(Boolean)
      .map((value) => String(value).trim()[0])
      .join("");

    if (letters) {
      return letters.slice(0, 2).toUpperCase();
    }

    return (userData?.email || "H")
      .split("")
      .filter((char: string) => /[a-z0-9]/i.test(char))
      .slice(0, 2)
      .join("")
      .toUpperCase();
  }, [userData]);

  const kycLabel = kycReady ? "KYC ready" : "KYC pending";
  const profileLabel = profileCompletion >= 100 ? "Profile complete" : "Profile in progress";

  const handleLogout = () => {
    logout();
    window.history.replaceState(null, "", "/");
  };

  const currentFocus =
    selectedItem === "Profile"
      ? "Personal information, address, and trade name."
      : selectedItem === "KYC Verification"
        ? "Upload verification documents."
        : "Manage which businesses can view your wallet.";

  return (
    <AccountContainer>
      <AccountPageHeader>
        <div>
          <AccountTitle>Account</AccountTitle>
        </div>
        <AccountHeaderStatus>
          <StatusPill $tone={profileCompletion === 100 ? "emerald" : "amber"}>
            <Sparkles size={14} />
            {profileLabel}
          </StatusPill>
          <StatusPill $tone={kycReady ? "emerald" : "rose"}>
            <BadgeCheck size={14} />
            {kycLabel}
          </StatusPill>
        </AccountHeaderStatus>
      </AccountPageHeader>

      <AccountLayout>
        <SummaryRail>
          <SummaryCard>
            <AvatarBlock>
              <AvatarCircle>{initials}</AvatarCircle>
              <div>
                <SummaryLabel>Signed in as</SummaryLabel>
                <SummaryName>{fullName}</SummaryName>
                <SummaryEmail>{userData?.email || "Email not available"}</SummaryEmail>
              </div>
            </AvatarBlock>
          </SummaryCard>

          <SummaryCard>
            <SectionLabel>Profile completion</SectionLabel>
            <CompletionRow>
              <CompletionValue>{profileCompletion}%</CompletionValue>
              <CompletionMeta>{profileCompletion === 100 ? "Fully filled" : "Needs attention"}</CompletionMeta>
            </CompletionRow>
            <CompletionTrack aria-hidden="true">
              <CompletionFill $value={profileCompletion} />
            </CompletionTrack>
            <SummaryCopy>
              {missingFields.length > 0
                ? `Missing: ${missingFields.join(", ")}.`
                : "Everything looks ready in your profile."}
            </SummaryCopy>
          </SummaryCard>

          <SummaryCard>
            <SectionLabel>Verification badge</SectionLabel>
            <BadgeRow>
              <StatusPill $tone={kycReady ? "emerald" : "amber"}>{kycLabel}</StatusPill>
              <StatusPill $tone="slate">{kycUploadedCount}/2 documents</StatusPill>
            </BadgeRow>
            <SummaryCopy>
              {kycReady
                ? "Your identity documents are complete and ready for review."
                : "Upload the remaining document to complete the verification flow."}
            </SummaryCopy>
          </SummaryCard>

          <SummaryCard>
            <SectionLabel>Quick actions</SectionLabel>
            <QuickActions>
              <Button
                type="button"
                variant={isProfile ? "default" : "outline"}
                onClick={() => setSelectedItem("Profile")}
              >
                <UserRound size={15} />
                Edit profile
                <ChevronRight size={14} />
              </Button>
              <Button
                type="button"
                variant={isKYC ? "default" : "outline"}
                onClick={() => setSelectedItem("KYC Verification")}
              >
                <BadgeCheck size={15} />
                Open KYC
                <ChevronRight size={14} />
              </Button>
              <Button
                type="button"
                variant={isWalletAccess ? "default" : "outline"}
                onClick={() => setSelectedItem("Wallet Access")}
              >
                <ShieldCheck size={15} />
                Wallet access
                <ChevronRight size={14} />
              </Button>
              <Button type="button" variant="destructive" onClick={handleLogout}>
                <LogOut size={15} />
                Logout
              </Button>
            </QuickActions>
          </SummaryCard>

          <SummaryCard>
            <SectionLabel>Current focus</SectionLabel>
            <FocusTitle>{selectedItem}</FocusTitle>
            <SummaryCopy>{currentFocus}</SummaryCopy>
          </SummaryCard>
        </SummaryRail>

        <MainColumn>
          <StickyNavShell>
            <AccountNav>
              {NAVITEMS?.map(({ name }) => (
                <AccountNavItem
                  key={name}
                  selectedItem={selectedItem === name ? name : ""}
                  onClick={() => setSelectedItem(name)}
                >
                  {name}
                </AccountNavItem>
              ))}
            </AccountNav>
          </StickyNavShell>

          <PanelSurface>
            {isProfile ? <Profile /> : isKYC ? <KYCVerification /> : <WalletAccess />}
          </PanelSurface>
        </MainColumn>
      </AccountLayout>
    </AccountContainer>
  );
};

const AccountPageHeader = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0.5rem;
  margin-bottom: 1rem;

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const AccountTitle = styled.h1`
  margin: 0;
  font-size: clamp(1.7rem, 2vw, 2.4rem);
  line-height: 1.1;
  color: ${(props) => props.theme.colors.text};
`;

const AccountHeaderStatus = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const AccountLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 300px) minmax(0, 1fr);
  gap: 1.25rem;
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryRail = styled.aside`
  position: sticky;
  top: 1rem;
  align-self: start;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (max-width: 1024px) {
    position: static;
  }
`;

const SummaryCard = styled.section`
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(250, 251, 253, 0.98));
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 24px;
  box-shadow: 0 20px 48px rgba(15, 23, 42, 0.05);
  padding: 1.15rem;
`;

const AvatarBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 0.95rem;
`;

const AvatarCircle = styled.div`
  width: 3.25rem;
  height: 3.25rem;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, #e11d48, #fb7185);
  color: #fff;
  font-weight: 800;
  letter-spacing: 0.06em;
`;

const SummaryLabel = styled.p`
  margin: 0 0 0.2rem;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.22em;
  color: ${(props) => props.theme.colors.textSecondary};
`;

const SummaryName = styled.h2`
  margin: 0;
  font-size: 1.15rem;
  color: ${(props) => props.theme.colors.text};
`;

const SummaryEmail = styled.p`
  margin: 0.2rem 0 0;
  color: ${(props) => props.theme.colors.textSecondary};
  font-size: 0.88rem;
  line-height: 1.5;
  word-break: break-word;
`;

const SectionLabel = styled.p`
  margin: 0 0 0.55rem;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.22em;
  color: ${(props) => props.theme.colors.textSecondary};
`;

const CompletionRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.65rem;
`;

const CompletionValue = styled.p`
  margin: 0;
  font-size: 1.8rem;
  line-height: 1;
  font-weight: 800;
  color: ${(props) => props.theme.colors.text};
`;

const CompletionMeta = styled.p`
  margin: 0;
  color: ${(props) => props.theme.colors.textSecondary};
  font-size: 0.82rem;
`;

const CompletionTrack = styled.div`
  width: 100%;
  height: 0.6rem;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.15);
  overflow: hidden;
`;

const CompletionFill = styled.div<{ $value: number }>`
  height: 100%;
  width: ${(props) => props.$value}%;
  border-radius: inherit;
  background: linear-gradient(90deg, #e11d48, #fb7185);
`;

const SummaryCopy = styled.p`
  margin: 0.75rem 0 0;
  color: ${(props) => props.theme.colors.textSecondary};
  font-size: 0.88rem;
  line-height: 1.6;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const StatusPill = styled.span<{ $tone: "emerald" | "amber" | "rose" | "slate" }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.8rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1;
  background: ${(props) => {
    switch (props.$tone) {
      case "emerald":
        return "rgba(16, 185, 129, 0.12)";
      case "amber":
        return "rgba(245, 158, 11, 0.12)";
      case "rose":
        return "rgba(225, 29, 72, 0.12)";
      default:
        return "rgba(100, 116, 139, 0.12)";
    }
  }};
  color: ${(props) => {
    switch (props.$tone) {
      case "emerald":
        return "#047857";
      case "amber":
        return "#b45309";
      case "rose":
        return "#be123c";
      default:
        return "#475569";
    }
  }};
`;

const QuickActions = styled.div`
  display: grid;
  gap: 0.65rem;

  button {
    justify-content: space-between;
    width: 100%;
  }
`;

const FocusTitle = styled.h3`
  margin: 0;
  color: ${(props) => props.theme.colors.text};
  font-size: 1.05rem;
`;

const MainColumn = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const StickyNavShell = styled.div`
  position: sticky;
  top: 1rem;
  z-index: 20;
  padding-top: 0.15rem;
  padding-bottom: 0.4rem;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.99), rgba(255, 255, 255, 0.86));
  backdrop-filter: blur(16px);
`;

const PanelSurface = styled.div`
  min-width: 0;
`;

export default Account;
